import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { readableTypeName } from '@iotv/core-services';
import { Assembler, edgeTypeMatrix } from '@iotv/datamodel';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { DeleteDialogManagaged } from '../../../ArbitrationObjects/DeletionArbitrator/DeleteDialogManaged';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { useMostProximateCustomer } from '../../../hooks/useMostProximateCustomer';
import { ActionIcons } from '../../../Iconography';
import useQuestion from '../../../Popup/useQuestion';
import { getViewDefinitionForNewObject } from '../../../v2/components/ExceptionTypesHandling/viewDefinitions';
import { standardCreateAndLinkItem, standardDeleteItem } from '../../../v2/components/generic/ObjectTree/standardTransactionVariantFns';
import { ObjectDetailCard } from '../../../v2/components/generic/ObjectView/ObjectDetailCard';
import { DialogWrapper } from '../../common/DiaglogWrapper';
import { ItemLink } from '../../common/ItemLinkList';
import { setDefaultValues } from '../../factories/AttributeDefinitions';
const { NODE_ENV, REACT_APP_DEPLOYMENT_STAGE: stage, REACT_APP_DEBUG } = process.env;
const isDevelopment = NODE_ENV === 'development' || stage === 'dev';
const debug = REACT_APP_DEBUG && false;
// repeated from @iotv/datamodel-core to debug
const getAllChildEdgeTypeMatrixEntries = (modelTypeOut) => {
    const imposterMatrix = edgeTypeMatrix;
    const edgeMatrixEntries = {};
    const possibleModelTypeInEntries = imposterMatrix[modelTypeOut] ?? {};
    Object.entries(possibleModelTypeInEntries).forEach(([modelTypeIn, edgeTypeMap]) => Object.assign(edgeMatrixEntries, { [modelTypeIn]: Object.values(edgeTypeMap) }));
    debug && console.log('edgeMatrixEntries', edgeMatrixEntries);
    return edgeMatrixEntries;
};
const defaultState = {
    contextObject: null,
    edgeTypeDefinitions: null,
    popupVisible: false,
    objectInCreation: null,
    edgeTypeIdForCreation: null,
    mutatedTransactionFunctions: null,
};
const filterEdgeTypeDefinition = (edgeTypeDefinitions, edgeTypeIdFilter) => {
    return Object.fromEntries(Object.entries(edgeTypeDefinitions).map(([outVType, edgeTypeArr]) => {
        return [outVType, edgeTypeArr.filter((v) => edgeTypeIdFilter.includes(v.edgeTypeId))];
    }));
};
const ContextMenuReducer = (prev, next) => {
    debug && console.log(`ContextMenuReducer got update`, next);
    const updated = { ...prev, ...next };
    debug && console.log('ContextMenuReducer is', updated);
    return updated;
};
export const ContextMenu = ({ contextObject, label, popUpElement, edgeTypeIdFilter, postTransactionCallbacks, ascendentAccessor, defaultCreationProperties, hideAdd = false, hideDelete = false, }) => {
    const [state, dispatch] = useReducer(ContextMenuReducer, defaultState);
    const thisRef = useRef(null);
    const getEdgeTypeDefinitions = () => {
        const edgeTypeDefinitions = getAllChildEdgeTypeMatrixEntries(contextObject.type);
        const filteredEdgeTypeDefs = edgeTypeIdFilter ? filterEdgeTypeDefinition(edgeTypeDefinitions, edgeTypeIdFilter) : edgeTypeDefinitions;
        return filteredEdgeTypeDefs;
    };
    useEffect(() => {
        if (contextObject) {
            const edgeTypeDefinitions = getEdgeTypeDefinitions();
            dispatch({ edgeTypeDefinitions, contextObject });
        }
    }, [contextObject?.sk]);
    return (_jsxs("div", { style: { display: 'flex', gap: 8 }, onContextMenu: (event) => {
            event.preventDefault();
            event.stopPropagation();
            dispatch({ popupVisible: true });
        }, children: [label ? _jsx(Typography, { flexGrow: 1, children: label }) : _jsx(ItemLink, { item: contextObject }), _jsx(IconButton, { ref: thisRef, size: "small", style: { margin: 0, padding: 3 }, onClick: (event) => {
                    event.stopPropagation();
                    dispatch({ popupVisible: true });
                }, children: _jsx(ActionIcons.ContextMenu, { fontSize: "inherit" }) }), popUpElement ?? (_jsx(StandardContextMenuContent, { anchorEl: thisRef.current,
                contextMenuState: state,
                dispatch,
                postTransactionCallbacks,
                ascendentAccessor,
                defaultCreationProperties,
                hideAdd,
                hideDelete }))] }));
};
const StandardContextMenuContent = ({ anchorEl, contextMenuState, dispatch, postTransactionCallbacks, ascendentAccessor, defaultCreationProperties, hideAdd = false, hideDelete = false, }) => {
    const { popupVisible: open, mutatedTransactionFunctions, edgeTypeDefinitions, contextObject, objectInCreation, edgeTypeIdForCreation } = contextMenuState;
    const [{ contextCustomer, TransactionFunctions, UserFunctions, contextUser, roleArbitrationInstance }] = useContext(AppMainViewContext);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [resolve, setResolve] = useState(() => () => { });
    const [confirmation, setConfirmation] = useState({ title: 'Confirm?', question: 'Are you sure?' });
    const { Dialogue, setOpen } = useQuestion({ ...confirmation, result: (r) => resolve(r) });
    const getConfirmation = (props) => {
        setConfirmation(props);
        setOpen(true);
        return new Promise((resolve) => setResolve(() => resolve));
    };
    const mostProximateCustomer = useMostProximateCustomer({ item: contextObject });
    /** tentative as possible basis for pattern on standardMutations */
    const postTransactionCallback = async (res) => {
        const { err, data } = res;
        if (err) {
            debug && console.log('ContextMenuV2 received unsuccessful result from DetailCard', err.message);
        }
        if (data) {
            const [source, edge, target] = data;
            debug && console.log('ContextMenuV2 received successful result from DetailCard', { data, postTransactionCallbacks, ascendentAccessor });
            dispatch({ objectInCreation: null, popupVisible: false });
            /** in this context menu linking aways happens with the new object as target on edge, so it is safe to assume the requested new object is an outV */
            if (target && ascendentAccessor && postTransactionCallbacks?.createAndLinkItem) {
                postTransactionCallbacks.createAndLinkItem({ vob: target, ascendentAccessor });
            }
        }
    };
    useEffect(() => {
        if (contextObject && edgeTypeDefinitions) {
            if (edgeTypeIdForCreation) {
                const mutatedTransactionFunctions = {
                    ...TransactionFunctions,
                    saveObject: (vob) => standardCreateAndLinkItem({ contextObject, vob, edgeTypeId: edgeTypeIdForCreation, postTransactionCallback }),
                };
                dispatch({ contextObject, mutatedTransactionFunctions });
            }
            else {
                console.log(`WARN: no edgeTypeIdForCreation`);
            }
        }
    }, [edgeTypeIdForCreation]);
    // If we want to use filter function customization we'll need to revamp the edgeTypeMatrix because this data comes from traversing it
    const getAllowableActions = (objectTypeId) => contextObject ? roleArbitrationInstance?.getObjectControlActionsByType({ sourceObjectType: contextObject.type, targetObjectType: objectTypeId }) : null;
    const edgeTypeAdds = Object.entries(edgeTypeDefinitions ?? {})
        .sort()
        .filter(([outVType]) => {
        /** This is intentionally limited to `Add`, as the context menu should not offer link, move, etc. which are complex operations */
        return getAllowableActions(outVType)?.add;
    });
    const hasAdds = edgeTypeAdds.map(([_, edgeTypeDefMaps]) => edgeTypeDefMaps).filter((a) => a?.length).length > 0;
    const handleCreateObjectRequest = ({ contextObject, objectTypeId, edgeTypeId }) => {
        if (contextObject) {
            const newObject = Assembler.getInstance({ type: objectTypeId, ...defaultCreationProperties });
            debug && console.log(`Creating new object with id ${newObject.id}`);
            setDefaultValues(newObject, getViewDefinitionForNewObject(newObject) ?? {});
            if (contextObject.type === 'Group' && edgeTypeId.includes(`_has_group`) && objectTypeId === 'Group') {
                newObject.supportedType = contextObject.supportedType;
            }
            dispatch({ objectInCreation: newObject, edgeTypeIdForCreation: edgeTypeId, popupVisible: false });
        }
        else {
            throw new Error('No ContextObject in handleCreateObjectRequest');
        }
    };
    const handleDeleteObject = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ popupVisible: false });
        setDeleteDialogOpen(true);
    };
    const newObjectCard = objectInCreation && mutatedTransactionFunctions ? (_jsx(ObjectDetailCard, { viewDefinition: getViewDefinitionForNewObject(objectInCreation),
        matchedPrimary: objectInCreation,
        contextObjectForDetailCard: contextCustomer,
        mostProximateCustomer,
        subContextCustomer: mostProximateCustomer,
        userFunctions: UserFunctions,
        transactionFunctions: mutatedTransactionFunctions,
        allowedActions: contextObject
            ? roleArbitrationInstance?.getObjectControlActionsByType({ sourceObjectType: contextObject.type, targetObjectType: objectInCreation.type })
            : roleArbitrationInstance?.getSingleObjectControlActions(objectInCreation.type) })) : null;
    const allowDeletion = ascendentAccessor &&
        contextObject &&
        roleArbitrationInstance?.getObjectControlActionsByType({ sourceObjectType: ascendentAccessor.contextObject.type, targetObjectType: contextObject.type })
            ?.delete;
    return (_jsxs(_Fragment, { children: [_jsxs(Menu, { anchorEl: anchorEl, open: open, onClose: () => dispatch({ popupVisible: false }), onClick: (e) => e.stopPropagation(), children: [isDevelopment && (_jsx(MenuItem, { id: "ContextMenu", disabled: true, children: _jsxs(ListItemText, { sx: { fontStyle: 'italic' }, children: ["ContextMenu ", contextObject?.name] }) }, "ContextMenu")), isDevelopment && ((!hideAdd && hasAdds) || (!hideDelete && allowDeletion)) && _jsx(Divider, {}), (hideAdd || !hasAdds) && (hideDelete || !allowDeletion) && (_jsx(MenuItem, { id: "noAction", disabled: true, children: _jsx(ListItemText, { sx: { fontStyle: 'italic' }, children: "Nothing available to do" }) }, "noAction")), !hideAdd &&
                        (hasAdds ? (edgeTypeAdds.map(([outVType, edgeTypeDefinitions]) => edgeTypeDefinitions.map((edgeTypeDefinition) => {
                            const { edgeTypeId } = edgeTypeDefinition;
                            return (_jsxs(MenuItem, { id: edgeTypeId, onClick: () => handleCreateObjectRequest({ contextObject, edgeTypeId, objectTypeId: outVType }), children: [_jsx(ListItemIcon, { children: _jsx(ActionIcons.Create, {}) }), _jsx(ListItemText, { children: `Add ${readableTypeName(outVType)}${isDevelopment ? ` (${edgeTypeDefinition.labels.out} — ${edgeTypeDefinition.edgeTypeId})` : ''}` })] }, edgeTypeId));
                        }))) : (_jsx(MenuItem, { id: "noAdd", disabled: true, children: _jsx(ListItemText, { sx: { fontStyle: 'italic' }, children: "Nothing available to add" }) }, "noAdd"))), !hideAdd && hasAdds && !hideDelete && allowDeletion && _jsx(Divider, {}), !hideDelete && allowDeletion && (_jsxs(MenuItem, { id: "remove", onClick: handleDeleteObject, children: [_jsx(ListItemIcon, { children: _jsx(ActionIcons.Delete, {}) }), _jsx(ListItemText, { children: "Remove" })] }, "remove"))] }, 'ContextMenu'), _jsx(DialogWrapper, { header: `Add ${objectInCreation?.type ? readableTypeName(objectInCreation.type) : 'Entity'} to ${contextObject?.name}${debug ? ` (with edgeType: ${edgeTypeIdForCreation})` : ''}`, open: objectInCreation !== null, setClosed: (actioned) => {
                    debug && console.log('ContextMenu dialog wants to save new object:', actioned);
                    dispatch({ objectInCreation: null });
                }, captureClicks: true, children: newObjectCard }, 'constextMenuV2_newObjectDialog'), _jsx(DeleteDialogManagaged, { matchedPrimary: contextObject,
                open: deleteDialogOpen,
                handleClose: () => setDeleteDialogOpen(false),
                handleDelete: async () => {
                    if (contextUser) {
                        const { err, data } = await standardDeleteItem({ item: contextObject, contextUser, mostProximateCustomer, UserFunctions, getConfirmation });
                        if (data)
                            setDeleteDialogOpen(false);
                    }
                } }), Dialogue] }));
};
